import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, PenLine, Send, MessageCircle, Mail, CreditCard } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function EnrollmentForm() {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    tc_no: "",
    phone: "",
    email: "",
    program: "",
    start_date: "",
    instructor: "Zülfikar Benli",
    commitment_accepted: false,
    kvkk_accepted: false,
    signature_name: "",
  });
  const [state, setState] = useState({ loading: false, ok: false, error: "", createdAt: null });
  const [siteSettings, setSiteSettings] = useState({
    bank_account_holder: "Zülfikar Benli",
    bank_name: "Akbank",
    bank_iban: "TR31 0004 6010 4888 8000 0978 23",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSiteSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/courses").then((r) => {
      setCourses(r.data);
      const slugParam = searchParams.get("program");
      if (slugParam) {
        const match = r.data.find((c) => c.slug === slugParam);
        if (match) setField("program", match.title);
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const selectedCourse = useMemo(
    () => courses.find((c) => c.title === form.program) || null,
    [courses, form.program]
  );

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "", createdAt: null });
    try {
      const { data } = await api.post("/enrollments", form);
      setState({ loading: false, ok: true, error: "", createdAt: data.created_at });
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message, createdAt: null });
    }
  };

  if (state.ok) {
    const dateStr = state.createdAt ? new Date(state.createdAt).toLocaleString("tr-TR") : "";
    const waText = encodeURIComponent(
      `ZB Campus - Katılım formumu onayladım.\nAd Soyad: ${form.full_name}\nProgram: ${form.program}\nTarih: ${dateStr}`
    );
    const mailSubject = encodeURIComponent("ZB Campus - Katılım Onay Formu");
    const mailBody = encodeURIComponent(
      `Ad Soyad: ${form.full_name}\nProgram: ${form.program}\nBaşlangıç: ${form.start_date}\nOnay Tarihi: ${dateStr}`
    );
    return (
      <div className="container-x py-24 max-w-xl mx-auto text-center min-h-[70vh] flex flex-col items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold-30 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-7 h-7 text-gold" />
        </div>
        <h1 className="font-display text-3xl md:text-4xl mb-3">Kaydınız Onaylandı</h1>
        <p className="text-white/70 mb-1">Bu form dijital olarak imzalanmış ve zaman damgasıyla kayıt altına alınmıştır.</p>
        <p className="text-xs text-white/40 mb-10">{dateStr}</p>

        <div className="w-full bg-surface border border-gold-30 p-6 mb-8 text-left">
          <div className="text-xs uppercase tracking-widest text-gold mb-3">Ön Ödeme — Havale/EFT Bilgileri</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-white/40 text-xs">Hesap Sahibi</div>
              <div className="text-white">{siteSettings.bank_account_holder}</div>
            </div>
            <div>
              <div className="text-white/40 text-xs">Banka</div>
              <div className="text-white">{siteSettings.bank_name}</div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-white/40 text-xs">IBAN</div>
              <div className="text-white font-mono tracking-wide">{siteSettings.bank_iban}</div>
            </div>
          </div>
          {selectedCourse?.payment_ref_code && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-white/40 text-xs mb-1">Açıklama kısmına yazın</div>
              <div className="font-mono text-gold text-lg tracking-wider">{selectedCourse.payment_ref_code}</div>
            </div>
          )}
          <p className="text-xs text-white/50 mt-4">
            Taahhütnamede belirtilen ön ödemeyi bu hesaba yapıp dekontu WhatsApp veya e-posta ile iletmeniz yeterlidir.
          </p>
          {selectedCourse?.shopier_url && (
            <a
              href={selectedCourse.shopier_url}
              target="_blank" rel="noreferrer"
              className="mt-4 flex items-center gap-2 text-xs text-white/50 hover:text-gold"
            >
              <CreditCard className="w-3.5 h-3.5" /> Kredi kartıyla ödemeyi tercih ederseniz Shopier üzerinden ödeyin
            </a>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <a
            href={`https://wa.me/905543291119?text=${waText}`}
            target="_blank" rel="noreferrer"
            className="btn-primary flex-1 justify-center text-sm"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp ile Gönder
          </a>
          <a
            href={`mailto:info@zbcampus.com?subject=${mailSubject}&body=${mailBody}`}
            className="btn-outline flex-1 justify-center text-sm"
          >
            <Mail className="w-4 h-4" /> E-posta ile Gönder
          </a>
        </div>
        <Link to="/" className="text-xs text-white/40 hover:text-gold mt-10">Ana sayfaya dön</Link>
      </div>
    );
  }

  return (
    <div className="container-x py-16 md:py-24 max-w-3xl mx-auto">
      <span className="section-eyebrow">Dijital Katılım Onay ve Taahhüt Formu</span>
      <h1 className="font-display text-4xl md:text-5xl mt-3 mb-4">Katılım <span className="italic gold-gradient-text">formu</span>.</h1>
      <p className="text-white/70 mb-12 max-w-xl">
        Lütfen bilgilerinizi eksiksiz doldurun. Onayınız zaman damgası ile kayıt altına alınır.
      </p>

      <form onSubmit={submit} data-testid="enrollment-form" className="space-y-10">
        {state.error && (
          <div className="bg-red-950/40 border border-red-800 text-red-200 p-4 text-sm">{state.error}</div>
        )}

        <Section step="01" title="Katılımcı Bilgileri">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Ad Soyad" required>
              <input required value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} className="input-style" />
            </Field>
            <Field label="T.C. Kimlik No" required>
              <input required maxLength={11} value={form.tc_no} onChange={(e) => setField("tc_no", e.target.value.replace(/\D/g, ""))} className="input-style" />
            </Field>
            <Field label="Telefon" required>
              <input required value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="input-style" />
            </Field>
            <Field label="E-posta" required>
              <input required type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="input-style" />
            </Field>
          </div>
        </Section>

        <Section step="02" title="Eğitim Bilgileri">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Program" required>
              <select required value={form.program} onChange={(e) => setField("program", e.target.value)} className="input-style">
                <option value="">Seçiniz</option>
                {courses.map((c) => <option key={c.id} value={c.title}>{c.title}</option>)}
              </select>
            </Field>
            <Field label="Başlangıç Tarihi">
              <input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} className="input-style" />
            </Field>
            <Field label="Eğitmen">
              <input value={form.instructor} onChange={(e) => setField("instructor", e.target.value)} className="input-style" />
            </Field>
          </div>
        </Section>

        <Section step="03" title="Katılımcı Taahhütnamesi">
          <ol className="space-y-3 text-sm text-white/75 list-decimal list-inside mb-5">
            <li>Eğitim programının içeriği, süresi, işlenişi ve toplam ücreti hakkında bilgilendirildim.</li>
            <li>Kontenjanımın kesinleşmesi için <b className="text-white">500 TL ön ödeme</b> yaptığımı/yapacağımı ve bu ön ödemenin <b className="text-white">hiçbir koşulda iade edilmeyeceğini</b> kabul ediyorum.</li>
            <li>Belirlenen ödeme planına ve eğitim takvimine uyacağımı taahhüt ederim.</li>
            <li>Eğitim sürecinde paylaşılan materyallerin ZB Campus'a ait olduğunu ve izinsiz paylaşılamayacağını kabul ederim.</li>
          </ol>
          <Checkbox checked={form.commitment_accepted} onChange={(v) => setField("commitment_accepted", v)}>
            Yukarıdaki taahhütname maddelerini <b className="text-white">okudum, anladım ve kabul ediyorum.</b>
          </Checkbox>
        </Section>

        <Section step="04" title="KVKK Aydınlatma Metni">
          <p className="text-sm text-white/70 leading-relaxed mb-5">
            Bu formda paylaştığınız ad-soyad, T.C. kimlik numarası, telefon ve e-posta bilgileriniz; 6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında, yalnızca eğitim kaydınızın oluşturulması, tarafınızla iletişim kurulması ve ödeme takibinin yapılması amacıyla ZB Campus Muhasebe Akademisi tarafından işlenmekte ve saklanmaktadır. Verileriniz üçüncü kişi veya kurumlarla paylaşılmaz. Kanun'un 11. maddesi kapsamındaki haklarınızı kullanmak için info@zbcampus.com adresi üzerinden bize başvurabilirsiniz.
          </p>
          <Checkbox checked={form.kvkk_accepted} onChange={(v) => setField("kvkk_accepted", v)}>
            KVKK Aydınlatma Metni'ni okudum, kişisel verilerimin belirtilen amaçlarla işlenmesini <b className="text-white">kabul ediyorum.</b>
          </Checkbox>
        </Section>

        <Section step="05" title="Dijital İmza">
          <p className="text-sm text-white/60 mb-4">Adınızı ve soyadınızı aşağıya yazmanız, bu formu elektronik olarak imzaladığınız anlamına gelir.</p>
          <Field label="Ad Soyad (İmza)" required>
            <div className="relative">
              <PenLine className="w-4 h-4 text-gold/60 absolute left-3 top-1/2 -translate-y-1/2" />
              <input required value={form.signature_name} onChange={(e) => setField("signature_name", e.target.value)} className="input-style pl-9 font-display text-lg italic" />
            </div>
          </Field>
        </Section>

        <button
          type="submit"
          disabled={state.loading || !form.commitment_accepted || !form.kvkk_accepted}
          data-testid="enrollment-submit-btn"
          className="btn-primary w-full justify-center text-sm py-4 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" /> {state.loading ? "Gönderiliyor..." : "Formu Onayla ve Gönder"}
        </button>
        <p className="text-xs text-white/40 text-center">Lütfen tüm zorunlu alanları doldurun ve onay kutularını işaretleyin.</p>
      </form>

      <style>{`
        .input-style {
          width: 100%;
          background: #0F0F14;
          border: 1px solid rgba(255,255,255,0.08);
          padding: 0.75rem 1rem;
          color: white;
          font-family: 'Outfit', sans-serif;
          transition: border-color 0.2s ease;
          outline: none;
        }
        .input-style:focus { border-color: rgba(212,175,55,0.6); }
        .input-style::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
    </div>
  );
}

function Section({ step, title, children }) {
  return (
    <div className="bg-surface border border-white/10 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <span className="text-gold font-display text-lg">{step}</span>
        <h2 className="font-display text-xl">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-white/50 mb-2">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}

function Checkbox({ checked, onChange, children }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#D4AF37] shrink-0"
      />
      <span className="text-sm text-white/75 leading-relaxed">{children}</span>
    </label>
  );
}
